import { all } from 'rsvp';
import uuid from 'uuid-js';
import { inject as service } from '@ember/service';
import Enum from '@square/dashboard-utilities/utils/enum';
import TeamService from '@square/dashboard-data/clients/team-api';
import { isPresent, isBlank } from '@ember/utils';
import RESTAdapter from '@ember-data/adapter/rest';
import { TeamMember, TeamMemberFetchFieldScope, TeamMemberIncludes } from '@square/dashboard-protos/models/squareup/team_api/resources/datatypes';
const {
  ACTIVE
} = TeamMember.Status;
export const SERVER_SEARCH_LIMIT = 200;
export const DataType = new Enum('WAGE', 'PAYROLL_EMPLOYEE');
function createOverrides(adapterOptions = {}) {
  const overrides = {};
  if (adapterOptions.removePermissions) {
    overrides.employee_role_token = null;
    overrides.permission_set_id = null;
  }
  if (typeof adapterOptions.isAuthorizedUserOverride === 'boolean') {
    overrides.is_authorized_user = adapterOptions.isAuthorizedUserOverride;
  }
  if (typeof adapterOptions.activeOverride === 'boolean') {
    overrides.active = adapterOptions.activeOverride;
  }
  return overrides;
}
function serializeHashWithOverrides(store, type, snapshot) {
  const overrides = createOverrides(snapshot?.adapterOptions);
  const serializer = store.serializerFor(type.modelName);
  return serializer.serialize(snapshot, {
    overrides,
    includeId: true
  });
}
function parseAdapterOptions(snapshotRecordArray) {
  const adapterOptions = snapshotRecordArray.adapterOptions ?? {};
  const includeData = adapterOptions.includeData ?? [];
  const includeWageData = includeData.includes(DataType.WAGE);
  const includePayrollEmployee = includeData.includes(DataType.PAYROLL_EMPLOYEE);
  return {
    includeWageData,
    includePayrollEmployee
  };
}
function getFieldsToClear(data) {
  const result = [];
  if (!data.referenceId) {
    result.push('reference_id');
  }
  if (!data.phoneNumber) {
    result.push('phone_number');
  }
  if (!data.emailAddress) {
    result.push('email_address');
  }
  if (!data.permissionSetId) {
    result.push('permission_set_id');
  }
  return result;
}

// eslint-disable-next-line ember/no-classic-classes
export default RESTAdapter.extend({
  hasLoadedEmployeeData: false,
  employeeJobs: service('employees/employee-jobs'),
  persons: service('employees/persons'),
  payrollEmployee: service('payroll-employee'),
  teamManagementEventBus: service('employees/team-management-event-bus'),
  features: service(),
  shiftsSubscriptions: service(),
  shouldBackgroundReloadRecord() {
    return false;
  },
  shouldBackgroundReloadAll() {
    return false;
  },
  shouldReloadAll(store, snapshotRecordArray) {
    const {
      includeWageData,
      includePayrollEmployee
    } = parseAdapterOptions(snapshotRecordArray);
    const reloadIncludeData = includeWageData || includePayrollEmployee; // always reload included data from the server to avoid it from becoming stale
    const reloadEmployeeData = !this.hasLoadedEmployeeData;
    return reloadEmployeeData || reloadIncludeData;
  },
  /*
   * **********************************************************************************************
   * ⚠️  DEPRECATED: ATTENTION REQUIRED! ⚠️
   *
   * The Ember datastore and `findAll` method are being PHASED OUT as we embark on our journey
   * towards a more scalable future with React and pagination for team members.
   *
   * 🚨 CLIENT ALERT 🚨: Fetching ALL employees from the start is NO LONGER SUPPORTED,
   * as it fails to scale with large sellers and leads to performance bottlenecks.
   *
   * 🔥 ACTION NEEDED: Any logic requiring ALL employees MUST be migrated to backend processing.
   * The days of client-side mass data fetching are OVER!
   *
   * **********************************************************************************************
   */
  async findAll(store, type, sinceToken, snapshotRecordArray) {
    const {
      includeWageData,
      includePayrollEmployee
    } = parseAdapterOptions(snapshotRecordArray);
    if (this.hasLoadedEmployeeData) {
      const employees = store.peekAll('employee').map(({
        token
      }) => ({
        token
      }));
      await this._loadIncludedData(employees.mapBy('token'), {
        includeWageData,
        includePayrollEmployee
      });
      return {
        employees
      };
    } else {
      const searchTeamMembersQuery = this.features.isEnabled('employee/dashboard_active_only') ? {
        filter: {
          status: ACTIVE
        }
      } : null;
      const employees = await this._loadAllData({
        includeWageData,
        includePayrollEmployee,
        searchTeamMembersQuery
      });
      return {
        employees
      };
    }
  },
  async _loadAllData({
    includeWageData = false,
    includePayrollEmployee = false,
    searchTeamMembersQuery = null,
    numberOfPages = null,
    limit = SERVER_SEARCH_LIMIT
  } = {}) {
    const batches = [];
    const personsBatches = [];
    const promises = [];
    let cursor = null;
    let pageNumber = 0;
    do {
      const {
        nextCursor,
        employeeData,
        employeeTokens,
        personsData
      } = await this._loadEmployeeData(cursor, searchTeamMembersQuery, limit);
      batches.push(employeeData);
      personsBatches.push(personsData);
      if (includeWageData) {
        promises.push(this.employeeJobs.loadWageData(employeeTokens));
      }
      if (includePayrollEmployee) {
        promises.push(this.payrollEmployee.loadEmployees(employeeTokens));
      }
      cursor = nextCursor;
      pageNumber++;
    } while (isPresent(cursor) && (numberOfPages === null || pageNumber < numberOfPages));
    await all(promises);
    if (isBlank(cursor)) {
      this.set('hasLoadedEmployeeData', true);
    }
    this.persons.setPersons(personsBatches.flat());
    return batches.flat();
  },
  async _loadEmployeeData(cursor, searchTeamMembersQuery, limit) {
    const fetchPersons = this.features.isEnabled('employee/invite_by_phone');
    const fetchFields = [];
    fetchFields.push(TeamMemberFetchFieldScope.FetchField.ASSOCIATED_PRODUCTS);
    fetchPersons && fetchFields.push(TeamMemberFetchFieldScope.FetchField.PERSON_ID);
    const includeFields = [];
    fetchPersons && includeFields.push(TeamMemberIncludes.Include.PERSONS);

    // TODO: TFPLAT-455 Pin to SearchTeamMembers EG version once
    // that's rolled out and stable
    const {
      cursor: nextCursor,
      teamMembers,
      includedResources
    } = await TeamService.searchTeamMembers({
      cursor,
      limit,
      fetchFields,
      include: includeFields,
      query: searchTeamMembersQuery
    });
    const employeeTokens = teamMembers.mapBy('id');
    return {
      nextCursor,
      employeeData: teamMembers,
      employeeTokens,
      personsData: includedResources?.persons || []
    };
  },
  async _loadIncludedData(employeeTokens, {
    includeWageData,
    includePayrollEmployee
  }) {
    const promises = [];
    if (includeWageData) {
      promises.push(this.employeeJobs.loadWageData(employeeTokens));
    }
    if (includePayrollEmployee) {
      promises.push(this.payrollEmployee.loadEmployees(employeeTokens));
    }
    await all(promises);
  },
  // @param {int} limit - the maximum number of team members in a page. Defaults to `undefined` which uses the server limit.
  // @param {int} numberOfPages - the maximum number of pages to request. Defaults to `null` which pulls all pages.
  // @param {Object} searchTeamMembersQuery - Represents a SearchTeamMembersQuery object from the TeamMember proto. E.g. { filter: { locationIds: [], ... } }
  async query(store, type, {
    limit,
    numberOfPages = null,
    searchTeamMembersQuery = null
  } = {}) {
    const maxResults = limit && numberOfPages ? limit * numberOfPages : null;
    if (maxResults) {
      const existingEmployees = store.peekAll('employee');
      if (existingEmployees.length >= maxResults) {
        return {
          employees: existingEmployees.slice(0, maxResults).map(({
            token
          }) => ({
            token
          }))
        };
      }
    }
    const employees = await this._loadAllData({
      limit,
      numberOfPages,
      searchTeamMembersQuery
    });
    return {
      employees
    };
  },
  async createRecord(store, type, snapshot) {
    if (snapshot.adapterOptions?.preventSave) {
      throw new Error('A new employee should always be saved to the server');
    }
    const data = serializeHashWithOverrides(store, type, snapshot);
    const {
      teamMember
    } = await TeamService.createTeamMember({
      teamMember: data,
      idempotencyKey: uuid.create().toString()
    });

    // The cap depends on how many active team members the merchant has
    await this.shiftsSubscriptions.reloadForCap.perform();
    return {
      employee: teamMember
    };
  },
  async updateRecord(store, type, snapshot) {
    if (snapshot.adapterOptions?.preventSave) {
      return undefined;
    }
    const activeIsChanging = Object.prototype.hasOwnProperty.call(snapshot.record.changedAttributes(), 'active') || typeof snapshot.adapterOptions?.activeOverride === 'boolean';
    const data = serializeHashWithOverrides(store, type, snapshot);
    const fieldsToClear = getFieldsToClear(data);
    const {
      teamMember
    } = await TeamService.updateTeamMember({
      teamMemberId: data.id,
      teamMember: data,
      fieldsToClear
    });
    if (activeIsChanging) {
      // The cap depends on how many active team members the merchant has
      await this.shiftsSubscriptions.reloadForCap.perform();
    }
    if (this.features.isEnabled('employee/team_list_rebuild')) {
      this.teamManagementEventBus.sendEvent('EDIT_TEAM_MEMBER', teamMember.id);
    }
    return {
      employee: teamMember
    };
  },
  async findRecord(store, type, id) {
    const fetchFields = ['PERSON_ID', 'ASSOCIATED_PRODUCTS'];
    const includeFields = ['PERSONS'];
    const {
      teamMember,
      includedResources
    } = await TeamService.retrieveTeamMember({
      teamMemberId: id,
      fetchFields: fetchFields.join(','),
      include: includeFields.join(',')
    });
    this.persons.setPersons(includedResources?.persons || []);
    return {
      employee: teamMember
    };
  },
  queryRecord() {
    throw new Error('The queryRecord() method is not supported for the employee model.');
  }
});